import React from 'react';
import { useParams } from 'react-router-dom';
import { StyleSheet, View } from './components/base_components';

import Breadcrumb from './components/breadcrumb';
import { BusyOverlay, useBusy } from './components/busy_component';
import BoxPropList from './components/box_prop_list';
import TextButton from './components/buttons/text_button';
import TitleActionLine from './components/title_action_line';

import OrgStore from './stores/org_store';
import UserStore from './stores/user_store';

const styles = StyleSheet.create({
  bucketEditScreen: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  actionButton: {
    marginLeft: '1rem',
  },
});

export default function BucketEditScreen() {
  const [is_busy, setBusy, clearBusy] = useBusy();
  const params = useParams();
  const org_id = UserStore.getOrg()?.org_id;
  const s3_bucket_id = parseInt(params.s3_bucket_id ?? '0', 10) || 0;
  const org = OrgStore.useOrg(org_id);
  const [updates, setUpdates] = React.useState<any>({});
  const bucket = org?.s3_bucket_list?.find(
    (b) => b.s3_bucket_id === s3_bucket_id
  );

  React.useEffect(() => {
    OrgStore.fetch(org_id);
  }, [org_id]);

  function _onEnablePress() {
    if (setBusy()) {
      const opts = { org_id, s3_bucket_id, is_disabled: false };
      OrgStore.updateBucketStatus(opts, (err) => {
        if (err) {
          window.alert('Failed to enable bucket, please try again.');
        }
        clearBusy();
      });
    }
  }
  function _onDisablePress() {
    const yes = window.confirm(
      'Disabling this store my cause uploads on other endpoints, and will stop any lifecylce management on this endpoint.'
    );
    if (yes && setBusy()) {
      const opts = { org_id, s3_bucket_id, is_disabled: true };
      OrgStore.updateBucketStatus(opts, (err) => {
        if (err) {
          window.alert('Failed to disable bucket, please try again.');
        }
        clearBusy();
      });
    }
  }
  function _onSave(complete) {
    if (setBusy()) {
      const opts = Object.assign(
        { org_id, s3_bucket_id, is_default: bucket?.is_default },
        updates
      );
      if (opts.max_size) {
        opts.max_size = parseFloat(opts.max_size);
      }
      OrgStore.updateBucket(opts, (err) => {
        if (err === 412) {
          window.alert(
            'Failed to verify bucket parameters, please check your settings and try again.'
          );
        } else if (err) {
          window.alert('Failed to save updates, please try again.');
        } else {
          complete();
        }
        clearBusy();
      });
    }
  }
  let columns;
  if (bucket?.is_default) {
    columns = [
      [
        {
          label: 'Status',
          value: bucket?.is_disabled ? 'Disabled' : 'Enabled',
        },
        { label: 'Bucket Name', value: bucket?.s3_bucket },
        {
          key: 'max_size',
          label: 'Max Object Size',
          value: bucket?.max_size,
          emptyValue: '-',
          input: {
            placeholder: 'bytes',
            type: 'number',
          },
          required: true,
        },
      ],
    ];
  } else {
    columns = [
      [
        {
          label: 'Status',
          value: bucket?.is_disabled ? 'Disabled' : 'Enabled',
        },
        {
          key: 's3_bucket',
          label: 'Bucket Name',
          value: bucket?.s3_bucket,
          input: {
            placeholder: 'bucket',
          },
          required: true,
        },
        {
          key: 's3_bucket_region',
          label: 'S3 Region',
          value: bucket?.s3_bucket_region,
          input: {
            placeholder: 'region',
          },
          required: true,
        },
        {
          key: 's3_host',
          label: 'S3 Host',
          value: bucket?.s3_host,
          input: {
            placeholder: 'host',
          },
          required: true,
        },
        {
          key: 's3_bucket_prefix',
          label: 'S3 Bucket Prefix',
          value: bucket?.s3_bucket_prefix,
          input: {
            placeholder: 'prefix (optional)',
          },
        },
      ],
      [
        {
          key: 'aws_access_key_id',
          label: 'Access Key ID',
          value: '',
          emptyValue: '****',
          input: {
            placeholder: '****',
          },
        },
        {
          key: 'aws_secret_access_key',
          label: 'Secret Access Key',
          value: '',
          emptyValue: '****',
          input: {
            placeholder: '****',
          },
        },
        bucket?.is_disabled
          ? null
          : {
              key: 'max_size',
              label: 'Max Object Size',
              value: bucket?.max_size,
              emptyValue: '-',
              input: {
                placeholder: 'bytes',
                type: 'number',
              },
              required: true,
            },
      ].filter((n) => n),
    ];
  }

  return (
    <View style={styles.bucketEditScreen}>
      <Breadcrumb
        list={[
          { text: org?.org_name, url: `/org` },
          { text: 'Network Stores', url: `/org/bucket` },
          bucket?.s3_bucket,
        ]}
      />
      <TitleActionLine title={bucket?.s3_bucket}>
        {bucket?.is_disabled ? (
          <TextButton
            style={styles.actionButton}
            text='Enable'
            onPress={_onEnablePress}
          />
        ) : (
          <TextButton
            style={styles.actionButton}
            text='Disable'
            type='danger'
            onPress={_onDisablePress}
          />
        )}
      </TitleActionLine>
      <BoxPropList
        title='Store Properties'
        editable={!bucket?.is_disabled || !bucket?.is_default}
        updates={updates}
        onChange={setUpdates}
        onSave={_onSave}
        columns={columns}
      />
      <BusyOverlay isBusy={is_busy} />
    </View>
  );
}
